"use client"
import ButtonElement from "@/app/(site)/_components/GCMolecules/ButtonElement"
import RichTextElement from "@/app/(site)/_components/GCMolecules/RichTextElement"
import { bodyHeadingLevel } from "@/src/utils/a11y/headingLevel"
import { clsx } from "@/src/utils/clsx"
import {
    estimateLineCount,
    findMaxOverflowWidth
} from "@/src/utils/typography/estimateLineCount"
import { useEffect, useId, useRef, useState } from "react"
import { type PortableTextBlock } from "sanity"

type Props = {
    heading?: string
    headingTag?: "h1" | "h2"
    text?: string
    richText?: PortableTextBlock[]
    className?: string
    hasCtaBlocks?: boolean
    showMoreLabel?: string
    showLessLabel?: string
    headingWrapper?: "header" | "div"
}

const COLLAPSED_HEIGHT = 72
const MAX_LINES = 3
const DESKTOP_TEXT_WIDTH_PX = 448
const BELOW_LG_PADDING_PX = 32
const LG_BREAKPOINT_PX = 1024

export default function PLPHeadingText({
    heading,
    headingTag,
    text,
    richText,
    hasCtaBlocks,
    showMoreLabel,
    showLessLabel,
    className,
    headingWrapper: HeadingWrapper = "div"
}: Props) {
    const resolvedShowMoreLabel = showMoreLabel || "SHOW MORE"
    const resolvedShowLessLabel = showLessLabel || "SHOW LESS"

    const overflowAtDesktop =
        estimateLineCount({ text, richText }, DESKTOP_TEXT_WIDTH_PX) > MAX_LINES
    const maxOverflowWidthPx = findMaxOverflowWidth({ text, richText }, MAX_LINES)
    const safeOverflowViewportPx =
        maxOverflowWidthPx > 0 ? maxOverflowWidthPx + BELOW_LG_PADDING_PX : 0

    const instanceId = useId().replace(/:/g, "")
    const buttonDataId = `pht-${instanceId}`

    const [isExpanded, setIsExpanded] = useState(false)
    const [isLineClamped, setIsLineClamped] = useState(true)
    const [contentHeight, setContentHeight] = useState(0)
    const [doesOverflow, setDoesOverflow] = useState(
        () => overflowAtDesktop || maxOverflowWidthPx > 0
    )
    const [hasMeasured, setHasMeasured] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = contentRef.current
        if (!el) return

        const check = () => {
            setDoesOverflow(el.scrollHeight > COLLAPSED_HEIGHT)
            setHasMeasured(true)
        }
        check()

        const observer = new ResizeObserver(check)
        observer.observe(el)
        return () => observer.disconnect()
    }, [richText, text])

    const handleToggle = () => {
        const el = contentRef.current
        if (!el) return

        if (isExpanded) {
            setContentHeight(el.scrollHeight)
            requestAnimationFrame(() => {
                setIsExpanded(false)
            })
        } else {
            setContentHeight(el.scrollHeight)
            setIsLineClamped(false)
            setIsExpanded(true)
        }
    }

    const handleTransitionEnd = () => {
        if (!isExpanded) {
            setIsLineClamped(true)
        }
    }

    const lineClampClass = isLineClamped ? "line-clamp-3" : ""

    const Tag = headingTag || "h2"
    const hasContent = (richText && richText.length > 0) || !!text
    return (
        <HeadingWrapper
            className={clsx(
                "flex flex-col gap-16 pb-24 lg:gap-32",
                hasCtaBlocks
                    ? "lg:max-w-[566px] lg:pb-0 lg:pr-24"
                    : "justify-between lg:flex-row lg:items-start lg:gap-0",

                className
            )}
        >
            {heading?.trim() && (
                <Tag
                    className={clsx(
                        "Trim_Mono_Regular_POC_Header_24_130_1_400_uppercase lg:Trim_Mono_Regular_POC_Header_32_130_1_400_uppercase",
                        !hasCtaBlocks &&
                            "lg:w-[566px] lg:max-w-[566px] lg:flex-1 lg:pr-16",
                        "ml-[-3px]"
                    )}
                    style={{ wordBreak: "break-word" }}
                >
                    {heading}
                </Tag>
            )}
            {hasContent && (
                <div
                    className={clsx(
                        "flex flex-col gap-4",
                        hasCtaBlocks ? "lg:max-w-[448px]" : "lg:w-[448px] lg:shrink-0"
                    )}
                >
                    {richText && richText.length > 0 ? (
                        <div
                            ref={contentRef}
                            className={clsx("overflow-hidden", lineClampClass)}
                            style={
                                doesOverflow
                                    ? {
                                          maxHeight: isExpanded
                                              ? contentHeight
                                              : COLLAPSED_HEIGHT,
                                          transition: "max-height 300ms ease-in-out"
                                      }
                                    : undefined
                            }
                            onTransitionEnd={handleTransitionEnd}
                        >
                            <RichTextElement
                                richTextGroup={richText}
                                className="break-words text-col-text-tertiary Trade_Gothic_Text_16_150_0_300"
                                baseHeadingLevel={bodyHeadingLevel(!!heading?.trim())}
                            />
                        </div>
                    ) : (
                        <p
                            ref={contentRef}
                            className={clsx(
                                "overflow-hidden",
                                lineClampClass,
                                "break-words text-col-text-tertiary Trade_Gothic_Text_16_150_0_300"
                            )}
                            style={
                                doesOverflow
                                    ? {
                                          maxHeight: isExpanded
                                              ? contentHeight
                                              : COLLAPSED_HEIGHT,
                                          transition: "max-height 300ms ease-in-out"
                                      }
                                    : undefined
                            }
                            onTransitionEnd={handleTransitionEnd}
                        >
                            {text}
                        </p>
                    )}
                    {doesOverflow && (
                        <>
                            {!hasMeasured &&
                                safeOverflowViewportPx < LG_BREAKPOINT_PX - 1 && (
                                    <style
                                        dangerouslySetInnerHTML={{
                                            __html: `@media (min-width:${safeOverflowViewportPx + 1}px) and (max-width:${LG_BREAKPOINT_PX - 1}px){[data-pht="${buttonDataId}"]{display:none !important;}}`
                                        }}
                                    />
                                )}
                            <ButtonElement
                                data-pht={buttonDataId}
                                className={clsx(
                                    "Trim_Mono_POC_Text_14_130_0_400_uppercase",
                                    !hasMeasured && !overflowAtDesktop && "lg:hidden"
                                )}
                                removeFontCompensation={true}
                                theme="tertiary-black"
                                size={{ desktop: "small", mobile: "small" }}
                                type="button"
                                icon={isExpanded ? "minus" : "plus"}
                                text={
                                    isExpanded
                                        ? resolvedShowLessLabel
                                        : resolvedShowMoreLabel
                                }
                                onClick={handleToggle}
                            />
                        </>
                    )}
                </div>
            )}
        </HeadingWrapper>
    )
}
